import { Link } from 'react-aria-components';
import { Icon } from '../atoms/Icon';
import { cn } from '../lib/cn';
import type { TabBarItem } from './TabBar';
import { uiText } from '../i18n';

export interface SideNavProps {
  items: TabBarItem[];
  activeId: string;
  label?: string;
  className?: string;
}

/**
 * The same four destinations as the phone tab bar, as a rail on tablets and the wall.
 * Same order, same icons, same labels – only the position changes with the screen.
 */
export function SideNav({ items, activeId, label, className }: SideNavProps) {
  return (
    <nav
      aria-label={label ?? uiText().mainNav}
      className={cn('flex flex-col gap-gap-xs', className)}
    >
      {items.map((item) => {
        const active = item.id === activeId;
        return (
          <Link
            key={item.id}
            href={item.href}
            aria-current={active ? 'page' : undefined}
            className={cn(
              'relative flex min-h-touch-target flex-col items-center justify-center gap-gap-xs rounded-control px-inset-xs text-sm',
              'outline-none data-focus-visible:outline-3 data-focus-visible:outline-border-focus data-hovered:bg-action-quiet-hover',
              active
                ? 'bg-surface-raised font-bold text-text-primary shadow-raised'
                : 'font-medium text-text-muted',
            )}
          >
            {active && (
              <span
                aria-hidden
                className="absolute inset-y-3 left-0 w-1 rounded-chip bg-accent-primary"
              />
            )}
            <span className="relative">
              <Icon icon={item.icon} size="lg" weight={active ? 'fill' : 'regular'} />
              {item.badge ? (
                <span
                  aria-hidden
                  className="absolute -top-2 -right-3 min-w-5 rounded-chip bg-accent-primary px-1 text-center text-xs font-bold text-accent-on-accent"
                >
                  {item.badge}
                </span>
              ) : null}
            </span>
            {item.label}
            {item.badge ? (
              <span className="sr-only">{uiText().open(String(item.badge))}</span>
            ) : null}
          </Link>
        );
      })}
    </nav>
  );
}
